import type { Submission } from '@/types'
import { db } from './database'
import { hydrateBlob, serializeBlobField } from './blob'
import { runDbOperation } from './errors'
import {
  isStoragePressureError,
  logSyncTelemetry,
  toTelemetryErrorFields,
} from '../sync/telemetry'
import {
  getAudioContainerFromMimeType,
  detectAudioContainerFromBytes,
} from '../audio'

// Local audio bytes are kept on-device for this long after a recording syncs, so
// it stays playable offline; afterward they're dropped and playback falls back to
// the remote S3 object (see purgeExpiredAudioBlobs).
export const LOCAL_AUDIO_RETENTION_MS = 7 * 24 * 60 * 60 * 1000

function hydrateSubmissionBlob(submission: Submission): void {
  if (
    submission.type === 'audio' &&
    !submission.localBlob &&
    submission.localBlobData
  ) {
    submission.localBlob = hydrateBlob(
      submission.localBlobData,
      submission.localBlobType,
    )
  }
}

function logBlobValidation(submission: Submission): void {
  if (submission.type !== 'audio' || !submission.localBlob) return

  void (async () => {
    try {
      const localBlob = submission.localBlob as Blob | undefined | null
      if (!localBlob) return

      if (localBlob.size === 0) {
        console.warn('[TESTING] Blob has zero size', {
          id: submission.id,
          blobType: localBlob.type,
        })
        return
      }

      const blobArrayBuffer = await localBlob.arrayBuffer()
      const headerBytes = new Uint8Array(blobArrayBuffer.slice(0, 12))
      const headerString = String.fromCharCode(
        ...Array.from(headerBytes.slice(0, 4)),
      )
      const headerHex = Array.from(headerBytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ')
      const declaredContainer = getAudioContainerFromMimeType(localBlob.type)
      const detectedContainer = detectAudioContainerFromBytes(headerBytes)
      const hasContainerMismatch =
        declaredContainer !== 'unknown' &&
        detectedContainer !== 'unknown' &&
        declaredContainer !== detectedContainer

      console.log('[TESTING] Retrieved audio submission from IndexedDB', {
        id: submission.id,
        localBlobType: localBlob.type,
        localBlobSize: localBlob.size,
        arrayBufferSize: blobArrayBuffer.byteLength,
        headerString,
        headerHex,
        declaredContainer,
        detectedContainer,
        hasContainerMismatch,
      })

      if (
        declaredContainer === 'wav' &&
        detectedContainer !== 'unknown' &&
        detectedContainer !== 'wav'
      ) {
        console.warn('[TESTING] Declared WAV blob has mismatched header', {
          id: submission.id,
          expectedContainer: 'wav',
          detectedContainer,
          declaredContainer,
          localBlobType: localBlob.type,
          headerHex,
        })
      }
    } catch (blobValidationError) {
      console.warn('[TESTING] Failed to validate blob from IndexedDB', {
        id: submission.id,
        error:
          blobValidationError instanceof Error
            ? blobValidationError.message
            : String(blobValidationError),
      })
    }
  })()
}

export async function getSubmissions(
  limit?: number,
): Promise<Submission[]> {
  return runDbOperation(async () => {
    let collection = db.submissions.orderBy('createdAt').reverse()
    if (limit) {
      collection = collection.limit(limit)
    }
    const submissions = await collection.toArray()
    for (const submission of submissions) {
      hydrateSubmissionBlob(submission)
      logBlobValidation(submission)
    }
    return submissions
  })
}

export async function getSubmissionById(
  id: string,
): Promise<Submission | undefined> {
  return runDbOperation(async () => {
    const value = await db.submissions.get(id)
    if (value) {
      hydrateSubmissionBlob(value)
    }
    return value
  })
}

export async function saveSubmission(submission: Submission): Promise<void> {
  return runDbOperation(async () => {
    try {
      const toStore: Submission = { ...submission }
      if (submission.type === 'audio') {
        const { data, type } = await serializeBlobField(
          submission.localBlob,
          submission.localBlobType,
        )
        toStore.localBlob = null
        toStore.localBlobData = data
        toStore.localBlobType = type
      }
      await db.submissions.put(toStore)
    } catch (error) {
      if (isStoragePressureError(error)) {
        logSyncTelemetry({
          type: 'storage-pressure',
          scope: 'submissions',
          operation: 'saveSubmission',
          itemId: submission.id,
          ...toTelemetryErrorFields(error),
        })
      }
      throw error
    }
  })
}

export async function deleteSubmission(id: string): Promise<void> {
  return runDbOperation(async () => {
    const submission = await db.submissions.get(id)
    await db.submissions.delete(id)
    const sessionId = submission?.chunkSessionId
    if (sessionId) {
      await db.chunks.where('sessionId').equals(sessionId).delete()
    }
  })
}

export async function clearAllSubmissions(): Promise<void> {
  await db.submissions.clear()
}

/**
 * Drop the on-device audio bytes for a synced submission to reclaim storage;
 * playback then falls back to the remote S3 object. Uses a raw row update (not
 * saveSubmission, which would re-serialize the blob) and clears any retained
 * progressive-upload chunks for the session.
 */
export async function purgeLocalAudio(id: string): Promise<void> {
  return runDbOperation(async () => {
    const submission = await db.submissions.get(id)
    if (!submission || submission.type !== 'audio') return
    if (submission.localAudioPurged && !submission.localBlobData) return
    await db.submissions.update(id, {
      localBlob: null,
      localBlobData: null,
      localAudioPurged: true,
    })
    const sessionId = submission.chunkSessionId
    if (sessionId) {
      await db.chunks.where('sessionId').equals(sessionId).delete()
    }
  })
}

/**
 * Reclaim storage by purging local audio for submissions whose retention window
 * has elapsed — fully uploaded, processing `completed`, and older than
 * LOCAL_AUDIO_RETENTION_MS (anchored on syncedAt). Best-effort and idempotent:
 * non-completed/partial/failed recordings and in-window recordings are left alone.
 */
export async function purgeExpiredAudioBlobs(): Promise<void> {
  const now = Date.now()
  const submissions = await runDbOperation(() => db.submissions.toArray())
  for (const s of submissions) {
    if (s.type !== 'audio') continue
    if (s.uploadStatus !== 'uploaded' || s.processingStatus !== 'completed') continue
    if (!s.localBlobData) continue
    const anchor = s.syncedAt ?? s.createdAt
    const anchorMs = anchor ? new Date(anchor).getTime() : Number.NaN
    if (!Number.isFinite(anchorMs)) continue
    if (now - anchorMs < LOCAL_AUDIO_RETENTION_MS) continue
    try {
      await purgeLocalAudio(s.id)
    } catch (error) {
      console.warn('Failed to purge expired local audio', {
        id: s.id,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }
}
